import Modal from 'chameleon/ui-stack/ui/base/modal';
import Button from 'chameleon/ui-stack/ui/base/button';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { getLibraryDataPromise } from 'legacy/src/components/questionBank/QuizBuilder/utils/apiQuestionHandler';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import React, { useEffect, useState } from 'react';
import LeftModalContent from '../../../SharedAssessmentPage/LeftModalContent';
import { autoGenerateQuiz, generatePracticeQuiz } from '../../client';
import { getQuestionMetaData, transformQuestionsTags } from '../../utils';
import { isEmpty } from 'lodash';
import { QuestionsPopoverContent } from '../../GeneratePracticeQuizModal/components/QuestionTypesPopover';

import './styles.scss';

const PracticeQuizSummaryModal = (props) => {
  const {
    client,
    isVisible,
    setIsModalVisible,
    setGeneratePracticeQuizModalOpen,
    generatePracticeParams,
    tagCategoryId,
    masterSubjectId,
    subjectInfo,
    setCreateQuizSuccessData,
  } = props;
  const { title, tagsPerformance, mcqNum, frqNum, exclusionDays } =
    generatePracticeParams || {};
  const renderComponentsWarningMessage =
    tagsPerformance?.length > parseInt(mcqNum, 10) + parseInt(frqNum, 10);
  const renderFRQMCQBothNotSelected =
    parseInt(mcqNum, 10) === 0 || parseInt(frqNum, 10) === 0;
  const renderExclusionDaysWarningMessage = exclusionDays > 0;
  const [questionsMetaData, setQuestionsMetaData] = useState({});
  const [tags, setTags] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessages, setWarningMessages] = useState([]);
  const [shouldShowWarningMessage, setShouldShowWarningMessage] =
    useState(false);

  useEffect(async () => {
    if (isVisible) {
      const response = await generatePracticeQuiz(client, {
        tagCategoryId,
        masterSubjectId,
        ...generatePracticeParams,
      });

      if (!isEmpty(response.errors)) {
        setErrorMessage(response.errors[0]);
      }

      if (!isEmpty(response.warnings)) {
        setWarningMessages(response.warnings);
      }

      if (!isEmpty(response.questions)) {
        const transformedQuestionsTags = transformQuestionsTags(
          response.questions,
        );
        const questionMetaData = getQuestionMetaData(response.questions);
        setTags(transformedQuestionsTags);
        setQuestionsMetaData(questionMetaData);
      }
    }
  }, [generatePracticeParams, isVisible]);
  const renderErrorMessage = (errorMessage) => (
    <div className="grid grid-flow-col	items-center gap-x-2 bg-red-100 border border-red-500	rounded-s text-gray-900 p-5 text-base">
      <Icon
        accessibility={{
          ariaHidden: true,
        }}
        id="close"
        width="14"
        height="14"
        className="bg-red-500 text-white rounded-full p-1"
      />
      {errorMessage}
    </div>
  );

  const renderWarningMessage = (warningMessages) => (
    <div className="flex justify-between bg-yellow-100 border border-yellow-500 rounded-s text-gray-900 p-5 text-base mb-8">
      <div className="grid grid-flow-col auto-cols-max gap-x-2 desktop:w-7/12">
        <Icon
          accessibility={{
            ariaHidden: true,
          }}
          id="warning_triangle"
          width="20"
          height="20"
          className="pt-2 text-yellow-600"
        />
        <span className="grid pt-1">
          {!shouldShowWarningMessage && (
            <p className="m-0">{`There are ${warningMessages.length} warnings in this quiz`}</p>
          )}
          {shouldShowWarningMessage &&
            warningMessages.map((message) => <p className="m-0">{message}</p>)}
          {shouldShowWarningMessage && (
            <div className="desktop:w-7/12">
              <QuestionsPopoverContent
                isQuizSummaryPage
                renderComponentsWarningMessage={renderComponentsWarningMessage}
                renderFRQMCQBothNotSelected={renderFRQMCQBothNotSelected}
                renderExclusionDaysWarningMessage={
                  renderExclusionDaysWarningMessage
                }
              />
            </div>
          )}
        </span>
      </div>
      <Button
        className="text-blue-700 pt-1"
        isNaked
        onClick={() => setShouldShowWarningMessage(!shouldShowWarningMessage)}
      >
        {!shouldShowWarningMessage ? 'Show' : 'Hide'}
      </Button>
    </div>
  );

  const renderModalContent = () => {
    if (isEmpty(questionsMetaData) && errorMessage) {
      return renderErrorMessage(errorMessage);
    }
    if (isEmpty(questionsMetaData)) {
      return <LoadingSpinner />;
    }
    if (!setQuestionsMetaData || !tags) {
      return null;
    }
    return (
      <LeftModalContent
        createdBy=""
        lastModifiedDate=""
        itemSharedDate=""
        itemMetadata={questionsMetaData}
        tags={tags}
        userTimezone=""
        createdDate=""
        masterSubjectId={masterSubjectId}
      />
    );
  };

  const resetQuizSummary = () => {
    setQuestionsMetaData({});
    setTags({});
    setErrorMessage('');
    setWarningMessages('');
  };

  const handleGoBack = () => {
    setGeneratePracticeQuizModalOpen(true);
    setIsModalVisible(false);
    resetQuizSummary();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    resetQuizSummary();
  };

  const renderButtonContent = () => (
    <div className="flex justify-end items-center border-t border-gray-300 h-32 pr-3">
      <Button isNaked onClick={() => handleCancel()}>
        Cancel
      </Button>
      <Button isNaked onClick={() => handleGoBack()}>
        Go Back
      </Button>
      <Button
        disabled={isEmpty(questionsMetaData)}
        isPrimary
        onClick={() => handleCreateQuiz()}
        loading
      >
        Generate Quiz
      </Button>
    </div>
  );

  const handleCreateQuiz = async () => {
    const libraryResponse = await getLibraryDataPromise(
      subjectInfo.subjects[0].initId,
    );
    const libraryId =
      (libraryResponse && libraryResponse.data && libraryResponse.data.id) ||
      null;
    let questionIds = '';
    questionsMetaData.itemIds.forEach((itemId) => {
      questionIds += `${itemId},`;
    });
    const response = await autoGenerateQuiz(
      client,
      title,
      libraryId,
      questionIds,
    );
    if (!isEmpty(response)) {
      handleCancel();
      setCreateQuizSuccessData({
        isModalVisible: true,
        itemId: response.itemId,
      });
    }
  };

  return (
    isVisible && (
      <Modal
        size="big"
        onClose={() => setIsModalVisible(false)}
        title={`${title} Summary`}
        bottomContent={renderButtonContent()}
      >
        {!isEmpty(questionsMetaData) &&
          warningMessages.length > 0 &&
          renderWarningMessage(warningMessages)}
        {renderModalContent()}
      </Modal>
    )
  );
};

export default PracticeQuizSummaryModal;
